import { useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { useFormContext } from 'react-hook-form';
import { makeStyles } from '@material-ui/core/styles';
import NgpRow from '../../../../../form/NgpRow';
import { useTestTypeArrayContext } from '../../hooks/UseTestTypeContext';
import DynamicFieldRenderer from './DynamicFieldRenderer';
import { FIELD_TYPE } from './DynamicConfigConstants';
import { useWifiNetworks, useWifiTests } from './UseDynamicConfigData';
import TestFormSection from '../TestFormSection/TestFormSection';
import { isBlank } from '../../../../../properties/funcs';

const useStyles = makeStyles((theme) => ({
    columnContainer: {
        ...theme.mixins.flex.flexDirectionColumn,
        ...theme.mixins.flex.alignItemsFlexStart,
    },
}));

const FIELD_DEFAULT_NAME = 'default';

const DynamicFields = ({ section, sectionKey, ssidList, wifiTestList }) => {
    const classes = useStyles();
    const fieldsLength = (section.fields || []).length;

    // change direction from default row to column for section just contain 1 column
    // for section contain more than 1 column, change to default behavior row direction
    const containerDirectionClassName = fieldsLength <= 1 ? classes.columnContainer : '';

    return (section.fields || []).map((row, rIndex) => {
        const rowKey = `${sectionKey}-row-${rIndex}`;

        return (
            <NgpRow key={rowKey} className={containerDirectionClassName} largeSpacing>
                {(row || []).filter(Boolean).map((field) => (
                    <DynamicFieldRenderer
                        key={`${rowKey}-column-${field?.title}`}
                        field={field}
                        ssidList={ssidList}
                        wifiTestList={wifiTestList}
                    />
                ))}
            </NgpRow>
        );
    });
};

/**
 * Renders dynamic form sections driven by testType.testBuilder.details.sections.
 * Used for test types that do NOT have a custom directive (Web, VoIP)
 * but define their config UI via JSON section/field descriptors.
 *
 * Affected test types: bandwidth, latency, portAvailability, fTP, loss,
 * jitter, wifi, tFTP, wifiConnect, wifiConnectMultiTest, pathLatency, dHCP.
 * largeSpacing = true
 */

export default function TestDynamicConfigSection({ testId }) {
    const isCreateAction = isEmpty(testId);
    const { watch, setValue, getValues } = useFormContext();
    const testTypeId = watch('type');
    const testType = useTestTypeArrayContext(testTypeId, true);
    const sections = useMemo(() => testType?.testBuilder?.details?.sections || [], [testType]);

    // Fetch WiFi data only when sections require it
    const { ssidList } = useWifiNetworks(sections);
    const { wifiTestList } = useWifiTests(sections);

    // Apply testConfig field defaults from testType.testBuilder.details.sections.
    // Matches portal/ui assignDefaultFields() lines 488-499 (section/field iteration)
    // and init() line 213 (existing test backfill: _.extend({}, defaultTestConfig, existingTestConfig)).
    //
    // For NEW tests: sets all field defaults (password → {}, others → field.default).
    // For EXISTING tests: backfills missing fields only (isNil check preserves API values).
    // Uses setValue without shouldDirty to avoid false isDirty on mount.
    const defaultsAppliedRef = useRef(false);
    useEffect(() => {
        if (defaultsAppliedRef.current || isEmpty(sections)) return;
        defaultsAppliedRef.current = true;

        sections.forEach((section) => {
            (section.fields || []).forEach((row) => {
                (row || []).filter(Boolean).forEach((field) => {
                    const fieldPath = `testConfig.${field.title}`;
                    const currentValue = getValues(fieldPath);

                    if (field.type === FIELD_TYPE.PASSWORD) {
                        // portal/ui: password fields default to {} (encrypted storage placeholder)
                        if (isBlank(currentValue)) {
                            setValue(fieldPath, {});
                        }
                    } else if (isCreateAction && isBlank(currentValue) && Object.hasOwn(field, FIELD_DEFAULT_NAME)) {
                        // Just for create set default value
                        setValue(fieldPath, field.default);
                    }
                });
            });
        });
    }, [sections, getValues, setValue, isCreateAction]);

    if (isEmpty(sections)) {
        return null;
    }

    const emptyTitleFields = [];
    const notEmptyTitleSections = [];

    sections.forEach((section, sIndex) => {
        const sectionTitle = section?.title;
        const sectionKey = sectionTitle || `section-${sIndex}`;
        if (isEmpty(sectionTitle)) {
            emptyTitleFields.push(
                <DynamicFields
                    key={sectionKey}
                    section={section}
                    sectionKey={sectionKey}
                    ssidList={ssidList}
                    wifiTestList={wifiTestList}
                />
            );
        } else {
            notEmptyTitleSections.push(
                <TestFormSection key={sectionKey} title={sectionTitle}>
                    <DynamicFields
                        section={section}
                        sectionKey={sectionKey}
                        ssidList={ssidList}
                        wifiTestList={wifiTestList}
                    />
                </TestFormSection>
            );
        }
    });

    return (
        <>
            {emptyTitleFields.length > 0 && <TestFormSection>{emptyTitleFields}</TestFormSection>}
            {notEmptyTitleSections}
        </>
    );
}

TestDynamicConfigSection.prototype = {
    testId: PropTypes.string,
};

TestDynamicConfigSection.default = {
    testId: undefined,
};
